import shoelaceStyles from '../styles/shoelaceStyles';
import styles from '../styles/styles.scss';
import mapStyles from '../styles/mapStyles.css';
import smartmapsMapsStyles from '@smartmaps/maps/dist/smartmaps-maps.css';
import {css, html, LitElement, nothing, render, unsafeCSS} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {createRef, Ref, ref} from 'lit/directives/ref.js';
import {log} from '../controllers/LogController';
import {Feature, Point} from 'geojson';
import {when} from 'lit/directives/when.js';
import {ConfigurationState} from '../storage/ConfigurationState';
import {map, Map} from '@smartmaps/maps';
import 'leaflet.markercluster';
import L from 'leaflet';


// prevent tree shaking
export {};

@customElement('ca-list-map')
export class CaListMapElement extends LitElement {
  static override styles = [
    styles,
    shoelaceStyles,
    smartmapsMapsStyles,
    mapStyles,
    css`
      :host {
        display: block;
      }

      #wrap {
        position: relative;
      }

      #map {
        height: 70vh;
      }

      sl-spinner {
        font-size: 50px;
        --track-width: 5px;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 100
      }
    `,
    unsafeCSS(`.fullscreen-icon {
      background-image: url('${ConfigurationState.resolve('./assets/icons/icon-fullscreen.svg')}')}');
    }`),
  ];

  @property({type: Object})
  selectedFeature: Feature | null = null;
  mapRef: Ref<HTMLDivElement> = createRef();
  private _markerClusterGroup: L.MarkerClusterGroup = L.markerClusterGroup(
    {
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      removeOutsideVisibleBounds: true,
      animateAddingMarkers: false,
      singleMarkerMode: true,
      iconCreateFunction: function(cluster) {
        const childCount = cluster.getChildCount();

        if (ConfigurationState.configuration.useFaviconMapIcons && childCount === 1) {
          return <L.Icon>cluster.getAllChildMarkers()[0].options.icon;
        }

        return L.divIcon({
          html: '<div title="' + childCount + ' Firmen"><span>' + childCount + '</span></div>',
          className: `marker-cluster marker-cluster-${childCount < 60 ? 'medium' : 'large'}`,
          iconSize: [30, 30],
        });
      },
      maxClusterRadius: function(zoom) { // big zoom -> less cluster probability
        return zoom > 12
          ? 20
          : 40;
      },
    },
  );
  private map: Promise<Map> = new Promise(
    async (mapResolve, reject) => {
      log(this.constructor.name, 'new Promise');

      await new Promise((resolve) => {
        const io = new IntersectionObserver(
          async (records) => {
            if (
              records.some((x) => x.isIntersecting)
              && records.some((x) => x.boundingClientRect.height > 0)
            ) {
              log(this.constructor.name, 'intersecting');
              await this.updateComplete;
              io.disconnect();
              resolve(true);
            }
          },
          {rootMargin: '50%'},
        );
        io.observe(this);
      });

      log(this.constructor.name, 'calling initMap');
      this.initMap().then(map => {
        mapResolve(map);
      });
    },
  );
  private _markersLayer: L.GeoJSON | undefined;

  private _features: Feature[] | undefined;

  get features() {
    return this._features;
  }

  @property({type: Object})
  set features(features: Feature[] | undefined) {
    log(this.constructor.name, 'set features', features);
    this._features = features;

    if (this._markersLayer) {
      this._markerClusterGroup.removeLayer(this._markersLayer);
    }

    this._markersLayer = L.geoJson(
      features,
      {
        pointToLayer: (feature, latLng) => {
          const icon = feature.properties.favicon_path
            ? L.divIcon(
              {
                html: `
                    <div title="${feature.properties.company}">
                      <img alt=""
                          src="${ConfigurationState.pageDataUrl(feature.properties.favicon_path.substring(1))}"
                          style="width: 16px; height: auto; margin-top: 6px">
                    </div>`,
                className: 'marker-cluster marker-cluster-small',
                iconSize: [30, 30],
                popupAnchor: [0, -15],
              },
            )
            : L.divIcon(
              {
                html: `<div title="${feature.properties.company}"><span></span></div>`,
                className: 'marker-cluster marker-cluster-small',
                iconSize: [30, 30],
                popupAnchor: [0, -15],
              },
            );

          return L.marker(
            latLng,
            {
              icon: icon,
            },
          );
        },
        onEachFeature: (feature, layer) => {
          const divEl = document.createElement('div');
          const template = html`
            <p>
              ${when(
            feature.properties!.favicon_path,
            () => html`<img alt="" style="max-width:16px; vertical-align:top"
                                loading="lazy"
                                src="${ConfigurationState.pageDataUrl(feature.properties!.favicon_path.substring(1))}">
                `,
          )}
              <a href=""
                 @click=${(ev: Event) => {
            // geometry seems mixed up here in onEachFeature()
            // const workaroundPointFeature = {
            //   ...feature,
            //   geometry: {
            //     type: 'Point',
            //     coordinates: (<Point>feature.geometry).coordinates.reverse(),
            //   },
            // };

            // @ts-ignore
            log(
              this.constructor.name,
              'dispatching ca-feature-selected',
              feature,
              (<Point>feature?.geometry).coordinates,
            );
            this.dispatchEvent(
              new CustomEvent(
                'ca-feature-selected',
                {
                  detail: feature,
                  bubbles: true,
                  composed: true,
                },
              ),
            );
            ev.preventDefault();
          }}
              >
                <b>${feature.properties!.company}</b>
              </a>
            </p>
            <p>
              ${feature.properties!.address}
              <br>
              ${feature.properties!.zip}
              ${feature.properties!.city}
            </p>
          `;

          render(template, divEl, {host: this});

          layer.bindPopup(
            divEl,
            // Mustache.render(
            //   Suche4.settings.mapPopupTemplate,
            //   {
            //     item: item,
            //     detailUrl: Suche4.detailUrl(item.slug)
            //   }
            // )
            {
              minWidth: 240,
            },
          );
        },
      },
    );
    this._markerClusterGroup.addLayer(this._markersLayer);
  }

  override render() {
    return html`
      <div id="wrap">
        ${when(
      this.features,
      () => nothing,
      () => html`
            <sl-spinner></sl-spinner>
          `,
    )}

        <div id="map" ${ref(this.mapRef)}></div>
      </div>
    `;
  }

  showFeature(feature: Feature) {
    this.map.then((map) => {
      log(this.constructor.name, 'showFeature final', feature, (<Point>feature.geometry).coordinates);

      const slug = feature.properties?.slug;
      if (slug) {
        this._markersLayer?.eachLayer((layer) => {
          if ((<any>layer).feature?.properties?.slug === slug) {
            log(this.constructor.name, 'showFeature found marker', layer);
            this._markerClusterGroup.zoomToShowLayer(
              layer,
              () => {
                log(this.constructor.name, 'showFeature zoomToShowLayer', layer);
                layer.openPopup();
              },
            );
          }
        });
      }
    });
  }

  private async initMap(): Promise<Map> {
    log(this.constructor.name, 'initMap');

    return new Promise(resolve => {
      map(
        // <HTMLElement>document.getElementById('map'),
        this.mapRef.value!,
        ConfigurationState.configuration.smartmapsAccessToken,
        {
          center: [48.79, 9.34],
          zoom: 9,
          // fullscreenControl: true,
          scrollWheelZoom: true,
          doubleClickZoom: true,
          smartmapsLayersControl: false,
          zoomControl: true,
          colorScheme: 'light',
          gestureHandling: true,
          style: 'designmode',
          touchZoom: true,
        },
        // {
        //   center: [49.021273, 8.439316],
        //   zoom: 14,
        //   fullscreenControl: true,
        //   scrollWheelZoom: false,
        //   // dragging: !Suche4.isTouchDevice(),
        //   // tap: false,
        //   // touchZoom: true,
        //   // @todo: sleep: Suche4.isTouchDevice(),
        //   // time(ms) until map sleeps on mouseout
        //   // sleepTime: 750,
        //   // time(ms) until map wakes on mouseover
        //   // wakeTime: 750,
        //   // should the user receive wake instructions?
        //   sleepNote: true,
        //   // should hovering wake the map? (non-touch devices only)
        //   hoverToWake: false,
        //   // a message to inform users about waking the map
        //   wakeMessage: 'Aktivieren per Klick',
        //   wakeMessageTouch: 'Aktivieren per Klick',
        //   // a constructor for a control button
        //   // sleepButton: ym.Control.sleepMapControl
        //   // opacity for the sleeping map
        //   sleepOpacity: 1.0,
        // },
      ).then((map: Map) => {
        this._markerClusterGroup.addTo(map);

        ConfigurationState.configuration.loadGeoJSONs.forEach(
          (geoJsonConfig) => {
            log(this.constructor.name, 'geoJson', geoJsonConfig);
            const url = ConfigurationState.resolve('./assets/geoJSON/' + Object.keys(geoJsonConfig)[0]);
            fetch(url)
              .then(
                (response) => response.json(),
              )
              .then(
                (geoJson) => {
                  const styleUrl = ConfigurationState.resolve('./assets/geoJSON/' + Object.values(geoJsonConfig)[0]);
                  fetch(styleUrl)
                    .then(
                      (response) => response.json(),
                    ).then(
                    (style) => {
                      log(this.constructor.name, 'style', style);
                      L.geoJson(
                        geoJson,
                        {
                          style: feature => style,
                        },
                      ).addTo(map);
                    },
                  );
                },
              );
          },
        );

        map.invalidateSize();
        resolve(map);
      });
    });
  }
}
